import React from 'react';
import { AlertTriangle, Home, MessageCircle, RefreshCw } from 'lucide-react';
import '../../Styles/Layout/ErrorState.css';

interface ErrorStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

interface ErrorStateProps {
  eyebrow?: string;
  title: string;
  message: string;
  primaryAction?: ErrorStateAction;
  secondaryAction?: ErrorStateAction;
  supportHref?: string;
  error?: Error;
  compact?: boolean;
}

const ActionButton: React.FC<{ action: ErrorStateAction; variant: 'primary' | 'secondary' }> = ({ action, variant }) => {
  const className = `app-error-state-action ${variant}`;
  const icon = variant === 'primary' ? <RefreshCw size={16} /> : <Home size={16} />;

  if (action.href) {
    return (
      <a className={className} href={action.href}>
        {icon}
        <span>{action.label}</span>
      </a>
    );
  }

  return (
    <button type="button" className={className} onClick={action.onClick}>
      {icon}
      <span>{action.label}</span>
    </button>
  );
};

export const ErrorState: React.FC<ErrorStateProps> = ({
  eyebrow = 'Something needs attention',
  title,
  message,
  primaryAction,
  secondaryAction,
  supportHref = 'https://cognora.ca/contact',
  error,
  compact = false
}) => {
  return (
    <section className={`app-error-state ${compact ? 'compact' : ''}`} role="alert">
      <div className="app-error-state-icon" aria-hidden="true">
        <AlertTriangle size={22} />
      </div>

      <div className="app-error-state-copy">
        <p className="app-error-state-eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p>{message}</p>
      </div>

      {(primaryAction || secondaryAction || supportHref) && (
        <div className="app-error-state-actions">
          {primaryAction && <ActionButton action={primaryAction} variant="primary" />}
          {secondaryAction && <ActionButton action={secondaryAction} variant="secondary" />}
          {supportHref && (
            <a className="app-error-state-support" href={supportHref}>
              <MessageCircle size={15} />
              <span>Contact support</span>
            </a>
          )}
        </div>
      )}

      {process.env.NODE_ENV === 'development' && error && (
        <details className="app-error-state-details">
          <summary>Error details</summary>
          <pre>{error.stack || error.message}</pre>
        </details>
      )}
    </section>
  );
};
